import React from 'react';
import './TextblockVariantTabs.css';

/**
 * Tabs "Default · <Ort> · … · + Variante" of the opened textblock (underline tabs like the group and
 * template editors). tabs: [{ key, label, isNew, hasError }] — key 'default' or the location id of a variant.
 * Only the active tab can hold unsaved changes (switching asks to discard them), so `dirty` marks it.
 * Also used by the catalog editor on /flatrates ("Standard · <Ort> · + Abweichung"): `ariaLabel`, `addLabel`,
 * `addDisabled`, `addTitle` (tooltip of the add button, e.g. why it is disabled), `idPrefix` (tab ids
 * `<prefix>Tab-<key>`, panel id `<prefix>Panel`) and `hasError` (marks a tab with invalid input) are optional —
 * the defaults are the textblock editor's.
 */
function TextblockVariantTabs({
  tabs,
  activeKey,
  dirty = false,
  onSelect,
  onAdd,
  ariaLabel = 'Varianten des Textbausteins',
  addLabel = 'Variante',
  addDisabled = false,
  addTitle,
  idPrefix = 'textblockVariant',
}) {
  const tabId = (key) => `${idPrefix}Tab-${key}`;

  const handleKeyDown = (event, index) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    onSelect(next.key);
    document.getElementById(tabId(next.key))?.focus();
  };

  return (
    <div className="textblockVariantTabs">
      <div className="textblockVariantTabList" role="tablist" aria-label={ariaLabel}>
        {tabs.map((t, index) => {
          const selected = t.key === activeKey;
          return (
            <button
              key={t.key}
              id={tabId(t.key)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${idPrefix}Panel`}
              tabIndex={selected ? 0 : -1}
              className={`textblockVariantTab${selected ? ' isActive' : ''}`}
              onClick={() => onSelect(t.key)}
              onKeyDown={e => handleKeyDown(e, index)}
            >
              {t.key !== 'default' && (
                <span className="material-symbols-rounded textblockVariantTabIcon" aria-hidden="true">location_on</span>
              )}
              {t.label}
              {t.isNew && <span className="textblockVariantTabBadge">neu</span>}
              {t.hasError ? (
                <span className="textblockVariantTabMarker isError" title="Bitte prüfen" aria-label="fehlerhaft" />
              ) : selected && dirty && (
                <span className="textblockVariantTabMarker" title="Ungespeicherte Änderungen" aria-label="geändert" />
              )}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="textblockVariantTab textblockVariantAdd"
        onClick={onAdd}
        disabled={addDisabled}
        title={addTitle}
      >
        <span className="material-symbols-rounded textblockVariantTabIcon" aria-hidden="true">add</span>
        {addLabel}
      </button>
    </div>
  );
}

export default TextblockVariantTabs;
